import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Package, MessageCircle, Search, Phone, Share2, ChevronLeft, ChevronRight, Play, ArrowRight, Grid3X3 } from 'lucide-react';

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [settings, setSettings] = useState(null);
  const [imageIndexes, setImageIndexes] = useState({});
  const [loaded, setLoaded] = useState(false);
  const catalogueRef = useRef(null);
  const [visibleCards, setVisibleCards] = useState(new Set());


  const nextImage = (productId, totalImages, e) => {
    e.stopPropagation();
    setImageIndexes(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % totalImages
    }));
  };

  const prevImage = (productId, totalImages, e) => {
    e.stopPropagation();
    setImageIndexes(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const getImageUrl = (img) => {
    if (img.startsWith('data:')) return img;
    if (img.includes('/uploads/')) {
      const filename = img.split('/uploads/').pop();
      return `${API.replace('/api', '')}/uploads/${filename}`;
    }
    return `${API.replace('/api', '')}/uploads/${img}`;
  };

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchSettings();
    setTimeout(() => setLoaded(true), 100);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleCards(prev => new Set([...prev, entry.target.dataset.cardId]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const cards = document.querySelectorAll('[data-card-id]');
    cards.forEach(card => observer.observe(card));
    return () => observer.disconnect();
  }, [filteredProducts]);

  const filterProducts = useCallback(() => {
    let filtered = products;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }
    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    setFilteredProducts(filtered);
  }, [selectedCategory, searchQuery, products]);

  useEffect(() => {
    filterProducts();
  }, [filterProducts]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?status=published`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleWhatsAppClick = (product) => {
    const number = settings?.whatsapp_number || '918889915080';
    const message = `Hi, I'm interested in *${product.name}* (₹${product.price.toLocaleString('en-IN')})`;
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleShareProduct = (product) => {
    const category = getCategoryName(product.category_id);
    let imageUrl = '';
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      if (firstImage.startsWith('http')) {
        imageUrl = firstImage;
      } else if (!firstImage.startsWith('data:')) {
        imageUrl = `https://cat.badgeify.cloud/uploads/${firstImage.replace('/uploads/', '').replace('uploads/', '')}`;
      }
    }

    let message = imageUrl ? `${imageUrl}\n\n` : '';
    message += `*${product.name}* \u2014 Badgeify\n\n`;
    message += `Price: ₹${product.price.toLocaleString('en-IN')}\n`;
    message += `Category: ${category}\n\n`;
    const shortDesc = product.description && product.description.length > 120
      ? product.description.substring(0, 120) + '...'
      : (product.description || '');
    if (shortDesc) message += `${shortDesc}\n\n`;
    message += `Browse catalogue: https://cat.badgeify.cloud\n`;
    message += `Contact: +91 8889915080`;

    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    toast.success('Opening WhatsApp to share');
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const getProductCountForCategory = (categoryId) => {
    return products.filter(p => p.category_id === categoryId).length;
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    let videoId = null;
    if (url.includes('watch?v=')) videoId = url.split('watch?v=')[1].split('&')[0];
    else if (url.includes('youtu.be/')) videoId = url.split('youtu.be/')[1].split('?')[0];
    else if (url.includes('youtube.com/embed/')) videoId = url.split('embed/')[1].split('?')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const getInstagramEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/instagram\.com\/(reel|p)\/([A-Za-z0-9_-]+)/);
    if (match) return `https://www.instagram.com/${match[1]}/${match[2]}/embed`;
    return null;
  };

  const formatDescription = (text) => {
    if (!text) return '';
    const lines = text.split('\n');
    let html = '';
    let inList = false;
    lines.forEach((line) => {
      if (line.trim() === '') {
        if (inList) { html += '</ul>'; inList = false; }
        html += '<br/>';
        return;
      }
      if (line.trim().match(/^[-*\u2022]\s/)) {
        if (!inList) { html += '<ul class="list-disc ml-5 space-y-1 mt-2">'; inList = true; }
        const content = line.trim().substring(2);
        html += `<li class="text-gray-500">${formatInline(content)}</li>`;
      } else {
        if (inList) { html += '</ul>'; inList = false; }
        html += `<p class="mb-1.5">${formatInline(line)}</p>`;
      }
    });
    if (inList) html += '</ul>';
    return html;
  };

  const formatInline = (text) => {
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-800 font-semibold">$1</strong>');
    text = text.replace(/__(.+?)__/g, '<strong>$1</strong>');
    text = text.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    return text;
  };

  const categoryColors = [
    'from-emerald-50 to-emerald-100 border-emerald-200',
    'from-sky-50 to-sky-100 border-sky-200',
    'from-violet-50 to-violet-100 border-violet-200',
    'from-amber-50 to-amber-100 border-amber-200',
    'from-rose-50 to-rose-100 border-rose-200',
    'from-cyan-50 to-cyan-100 border-cyan-200',
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');

        * { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; }

        html { scroll-behavior: smooth; }

        /* --- ENTRY ANIMATIONS --- */
        .fade-in {
          animation: fadeIn 0.6s ease both;
        }
        .fade-in-up {
          animation: fadeInUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .fade-in-up-delay-1 { animation-delay: 0.1s; }
        .fade-in-up-delay-2 { animation-delay: 0.2s; }
        .fade-in-up-delay-3 { animation-delay: 0.3s; }
        .fade-in-up-delay-4 { animation-delay: 0.4s; }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* --- CARD REVEAL --- */
        .card-reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }
        .card-reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* --- PRODUCT CARD --- */
        .product-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px -12px rgba(0,0,0,0.12);
        }
        .product-card:hover .card-image {
          transform: scale(1.05);
        }
        .card-image {
          transition: transform 0.5s ease;
        }

        /* --- CAROUSEL --- */
        .carousel-arrow {
          opacity: 0;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .group:hover .carousel-arrow {
          opacity: 1;
        }
        .carousel-dot {
          transition: all 0.3s ease;
        }
        .carousel-dot.active {
          width: 18px;
          background: #1a1a1a;
        }

        /* --- CATEGORY CARD --- */
        .cat-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .cat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 30px -10px rgba(0,0,0,0.1);
        }

        /* --- NAV LINK --- */
        .nav-link {
          position: relative;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 1.5px;
          background: #1a1a1a;
          transition: width 0.3s ease;
        }
        .nav-link:hover::after {
          width: 100%;
        }

        /* --- FLOATING WHATSAPP --- */
        .float-wa {
          animation: floatBounce 3s ease-in-out infinite;
        }
        .float-wa:hover {
          animation-play-state: paused;
        }
        .float-wa::before {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 50%;
          background: rgba(37, 211, 102, 0.3);
          animation: waPulse 2s ease-in-out infinite;
        }
        @keyframes floatBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes waPulse {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.3); opacity: 0; }
        }

        /* --- PLAY PULSE --- */
        .play-pulse {
          animation: playPulse 2s ease-in-out infinite;
        }
        @keyframes playPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
        }

        /* --- FOOTER LINK --- */
        .footer-link {
          position: relative;
          display: inline-block;
        }
        .footer-link::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          width: 0;
          height: 1px;
          background: #6b7280;
          transition: width 0.3s ease;
        }
        .footer-link:hover::after {
          width: 100%;
        }

        /* --- MODAL --- */
        .modal-image {
          animation: modalImageIn 0.4s ease both;
        }
        @keyframes modalImageIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .modal-thumb {
          transition: all 0.2s ease;
        }
        .modal-thumb:hover {
          transform: scale(1.08);
        }
      `}</style>

      <div className="min-h-screen bg-white">

        {/* Floating WhatsApp */}
        <a
          href="https://wa.me/918889915080?text=Hi%2C%20I%27m%20interested%20in%20your%20products"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 float-wa"
        >
          <div className="relative bg-[#25D366] text-white rounded-full p-3.5 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300">
            <MessageCircle className="h-6 w-6 relative z-10" fill="white" />
          </div>
        </a>

        {/* ═══════════════ HEADER ═══════════════ */}
        <header className={`sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 transition-all duration-500 ${loaded ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 md:h-[72px]">

              {/* Logo */}
              <div className="flex items-center gap-3">
                {settings?.company_logo && (
                  <img
                    src={(() => {
                      const logo = settings.company_logo;
                      if (logo.startsWith('data:')) return logo;
                      if (logo.includes('/uploads/')) {
                        const filename = logo.split('/uploads/').pop();
                        return `${API.replace('/api', '')}/uploads/${filename}`;
                      }
                      return `${API.replace('/api', '')}/uploads/${logo}`;
                    })()}
                    alt="Badgeify"
                    className="h-9 md:h-11 w-auto object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <span className="text-xl md:text-2xl font-bold tracking-tight text-[#1a1a1a]">
                  {settings?.company_name || 'Copier Bazar'}
                </span>
              </div>

              {/* Nav Links - Desktop */}
              <nav className="hidden md:flex items-center gap-8">
                <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="nav-link text-sm text-gray-600 hover:text-[#1a1a1a] transition-colors">Home</button>
                <button onClick={() => document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' })} className="nav-link text-sm text-gray-600 hover:text-[#1a1a1a] transition-colors">Categories</button>
                <button onClick={() => document.getElementById('catalogue')?.scrollIntoView({ behavior: 'smooth' })} className="nav-link text-sm text-gray-600 hover:text-[#1a1a1a] transition-colors">Products</button>
                <button onClick={() => document.getElementById('footer')?.scrollIntoView({ behavior: 'smooth' })} className="nav-link text-sm text-gray-600 hover:text-[#1a1a1a] transition-colors">Contact</button>
              </nav>

              {/* Right */}
              <div className="flex items-center gap-3">
                <a href="tel:+918889915080" className="hidden lg:flex items-center gap-2 text-sm text-gray-500 hover:text-[#1a1a1a] transition-colors">
                  <Phone className="h-3.5 w-3.5" />
                  <span>+91 8889915080</span>
                </a>
                <a
                  href="https://wa.me/918889915080?text=Hi%2C%20I%20want%20to%20know%20about%20your%20products"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-[#25D366] hover:bg-[#20BD5A] text-white text-xs md:text-sm font-semibold h-9 px-4 rounded-lg transition-colors">
                    <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* ═══════════════ HERO ═══════════════ */}
        <section className={`relative overflow-hidden transition-all duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
          <div className="bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 lg:py-32 text-center">
              <p className="fade-in-up text-[11px] md:text-xs font-semibold tracking-[0.25em] uppercase text-gray-400 mb-5">
                Machines & Supplies
              </p>

              <h1 className="fade-in-up fade-in-up-delay-1 text-4xl md:text-5xl lg:text-[56px] font-bold text-[#1a1a1a] leading-[1.1] mb-6 max-w-3xl mx-auto">
                Badge & Magnet Making{' '}
                <span className="text-gray-400">Machines for Everyone</span>
              </h1>

              <p className="fade-in-up fade-in-up-delay-2 text-base md:text-lg text-gray-400 max-w-xl mx-auto mb-10 leading-relaxed">
                Professional-grade badge makers, fridge magnet machines, and premium raw materials. Everything you need to start or scale your production.
              </p>

              <div className="fade-in-up fade-in-up-delay-3 flex flex-wrap items-center justify-center gap-3">
                <Button
                  onClick={() => document.getElementById('catalogue')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-[#1a1a1a] hover:bg-[#333] text-white font-semibold px-7 h-12 text-sm rounded-lg transition-colors"
                >
                  Browse Catalogue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <a href="https://wa.me/918889915080?text=Hi%2C%20I%20want%20to%20know%20about%20your%20products" target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-semibold px-7 h-12 text-sm rounded-lg transition-colors"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════ CATEGORIES ═══════════════ */}
        {categories.length > 0 && (
          <section id="categories" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="mb-8">
              <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-400 mb-1">Shop by</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a]">Categories</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  document.getElementById('catalogue')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`cat-card group rounded-xl border p-6 text-left transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-[#1a1a1a] border-[#1a1a1a] text-white'
                    : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 hover:border-gray-300'
                }`}
              >
                <Grid3X3 className={`h-5 w-5 mb-3 ${selectedCategory === 'all' ? 'text-white/70' : 'text-gray-400'}`} />
                <h3 className={`font-semibold text-sm ${selectedCategory === 'all' ? 'text-white' : 'text-[#1a1a1a]'}`}>All Products</h3>
                <p className={`text-xs mt-1 ${selectedCategory === 'all' ? 'text-white/60' : 'text-gray-400'}`}>
                  {products.length} items
                </p>
              </button>

              {categories.map((cat, i) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    document.getElementById('catalogue')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`cat-card group rounded-xl border p-6 text-left transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-[#1a1a1a] border-[#1a1a1a] text-white'
                      : `bg-gradient-to-br ${categoryColors[i % categoryColors.length]} hover:shadow-sm`
                  }`}
                >
                  <Package className={`h-5 w-5 mb-3 ${selectedCategory === cat.id ? 'text-white/70' : 'text-gray-400'}`} />
                  <h3 className={`font-semibold text-sm ${selectedCategory === cat.id ? 'text-white' : 'text-[#1a1a1a]'}`}>{cat.name}</h3>
                  <p className={`text-xs mt-1 ${selectedCategory === cat.id ? 'text-white/60' : 'text-gray-400'}`}>
                    {getProductCountForCategory(cat.id)} items
                  </p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ═══════════════ PRODUCTS ═══════════════ */}
        <section id="catalogue" ref={catalogueRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">

          <div className="flex flex-col md:flex-row gap-4 items-start md:items-end justify-between mb-8">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-400 mb-1">We recommend</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a]">
                Our Products
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} available
              </p>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10 bg-gray-50 border-gray-200 focus:border-gray-400 focus:ring-gray-200 rounded-lg text-sm"
                />
              </div>

              {/* Mobile category dropdown */}
              <div className="md:hidden">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full bg-gray-50 border-gray-200 h-10 rounded-lg text-sm">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Product Grid */}
          {filteredProducts.length === 0 ? (
            <div className="text-center py-24 rounded-xl border border-dashed border-gray-200 bg-gray-50/50">
              <Package className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-400 font-medium">No products found</p>
              <p className="text-gray-300 text-sm mt-1">Try a different search or category</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredProducts.map((product, idx) => (
                <div
                  key={product.id}
                  data-card-id={product.id}
                  className={`card-reveal ${visibleCards.has(product.id) ? 'visible' : ''}`}
                  style={{ transitionDelay: `${(idx % 4) * 80}ms` }}
                >
                  <Card className="product-card overflow-hidden bg-white border border-gray-200 rounded-xl shadow-none">
                    {/* Image */}
                    <div className="relative aspect-square bg-[#f8f8f8] overflow-hidden group">
                      {product.images && product.images.length > 0 ? (
                        <>
                          <img
                            src={getImageUrl(product.images[imageIndexes[product.id] || 0])}
                            alt={product.name}
                            className="w-full h-full object-contain card-image p-3"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement.querySelector('.img-fallback');
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                          {product.images.length > 1 && (
                            <>
                              <button
                                onClick={(e) => prevImage(product.id, product.images.length, e)}
                                className="carousel-arrow absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1 shadow-sm"
                              >
                                <ChevronLeft className="h-4 w-4 text-gray-600" />
                              </button>
                              <button
                                onClick={(e) => nextImage(product.id, product.images.length, e)}
                                className="carousel-arrow absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1 shadow-sm"
                              >
                                <ChevronRight className="h-4 w-4 text-gray-600" />
                              </button>
                              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                                {product.images.map((_, i) => (
                                  <span
                                    key={i}
                                    className={`carousel-dot h-1.5 rounded-full ${
                                      (imageIndexes[product.id] || 0) === i
                                        ? 'active bg-[#1a1a1a]'
                                        : 'w-1.5 bg-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </>
                      ) : null}
                      <div
                        className="img-fallback w-full h-full flex items-center justify-center"
                        style={{ display: product.images && product.images.length > 0 ? 'none' : 'flex' }}
                      >
                        <Package className="h-12 w-12 text-gray-200" />
                      </div>

                      {/* Category Badge */}
                      <Badge className="absolute top-2.5 left-2.5 bg-white/90 backdrop-blur-sm text-gray-600 border-0 shadow-sm text-[10px] font-medium tracking-wide">
                        {getCategoryName(product.category_id)}
                      </Badge>

                      {(product.video || product.youtube_link || product.instagram_link) && (
                        <div className="play-pulse absolute top-2.5 right-2.5 bg-red-500 text-white rounded-full p-1.5 shadow-md">
                          <Play className="h-3 w-3" fill="white" />
                        </div>
                      )}
                    </div>

                    <CardContent className="p-4">
                      <h3 className="font-semibold text-[15px] text-[#1a1a1a] mb-1 leading-snug line-clamp-1">
                        {product.name}
                      </h3>

                      {product.description && (
                        <p className="text-xs text-gray-400 line-clamp-2 mb-3 leading-relaxed">
                          {product.description.replace(/\*\*/g, '').replace(/[-*\u2022]\s/g, '').substring(0, 80)}
                          {product.description.length > 80 ? '...' : ''}
                        </p>
                      )}

                      {/* Price */}
                      <p className="text-lg font-bold text-[#16a34a] mb-3">
                        ₹{product.price.toLocaleString('en-IN')}
                      </p>

                      {/* Buttons */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleWhatsAppClick(product)}
                          className="flex-1 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold h-9 rounded-lg text-xs transition-colors"
                        >
                          <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
                          Enquire
                        </Button>

                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="flex-1 border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 font-medium h-9 rounded-lg text-xs transition-colors"
                            >
                              Details
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border-gray-200">
                            <DialogHeader>
                              <DialogTitle className="text-2xl font-bold text-[#1a1a1a]">
                                {product.name}
                              </DialogTitle>
                            </DialogHeader>

                            <div className="space-y-5">
                              {/* Gallery */}
                              {product.images && product.images.length > 0 && (
                                <div className="space-y-3">
                                  <div className="relative bg-[#f8f8f8] rounded-xl overflow-hidden border border-gray-100">
                                    <img
                                      key={imageIndexes[`modal-${product.id}`] || 0}
                                      src={getImageUrl(product.images[imageIndexes[`modal-${product.id}`] || 0])}
                                      alt={product.name}
                                      className="modal-image w-full h-64 md:h-80 object-contain p-4"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    {product.images.length > 1 && (
                                      <>
                                        <button
                                          onClick={(e) => prevImage(`modal-${product.id}`, product.images.length, e)}
                                          className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md"
                                        >
                                          <ChevronLeft className="h-5 w-5 text-gray-600" />
                                        </button>
                                        <button
                                          onClick={(e) => nextImage(`modal-${product.id}`, product.images.length, e)}
                                          className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md"
                                        >
                                          <ChevronRight className="h-5 w-5 text-gray-600" />
                                        </button>
                                      </>
                                    )}
                                  </div>

                                  {product.images.length > 1 && (
                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                      {product.images.map((img, idx) => (
                                        <button
                                          key={idx}
                                          onClick={() => setImageIndexes(prev => ({ ...prev, [`modal-${product.id}`]: idx }))}
                                          className={`modal-thumb flex-shrink-0 w-16 h-16 rounded-lg border-2 overflow-hidden ${
                                            (imageIndexes[`modal-${product.id}`] || 0) === idx
                                              ? 'border-[#1a1a1a]'
                                              : 'border-gray-200 hover:border-gray-300'
                                          }`}
                                        >
                                          <img
                                            src={getImageUrl(img)}
                                            alt={`${product.name} ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                          />
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Info */}
                              <div className="flex items-center gap-3">
                                <Badge className="bg-gray-100 text-gray-600 border-gray-200 font-medium text-xs">
                                  {getCategoryName(product.category_id)}
                                </Badge>
                                <span className="text-2xl font-bold text-[#16a34a]">
                                  ₹{product.price.toLocaleString('en-IN')}
                                </span>
                              </div>

                              {/* Description */}
                              {product.description && (
                                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400 mb-3">Description</p>
                                  <div
                                    className="text-gray-500 text-sm leading-relaxed prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: formatDescription(product.description) }}
                                  />
                                </div>
                              )}

                              {/* Uploaded Video */}
                              {product.video && (
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400 mb-3">Product Video</p>
                                  <div className="rounded-xl overflow-hidden border border-gray-200 bg-black">
                                    <video
                                      src={getImageUrl(product.video)}
                                      controls
                                      className="w-full max-h-80"
                                      preload="metadata"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* YouTube */}
                              {product.youtube_link && getYouTubeEmbedUrl(product.youtube_link) && (
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400 mb-3">YouTube</p>
                                  <div className="aspect-video rounded-xl overflow-hidden border border-gray-200">
                                    <iframe
                                      width="100%"
                                      height="100%"
                                      src={getYouTubeEmbedUrl(product.youtube_link)}
                                      title="Product video"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Instagram */}
                              {product.instagram_link && getInstagramEmbedUrl(product.instagram_link) && (
                                <div>
                                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400 mb-3">Instagram</p>
                                  <div className="rounded-xl overflow-hidden border border-gray-200" style={{ maxHeight: '500px' }}>
                                    <iframe
                                      width="100%"
                                      height="480"
                                      src={getInstagramEmbedUrl(product.instagram_link)}
                                      title="Instagram post"
                                      frameBorder="0"
                                      scrolling="no"
                                      allowtransparency="true"
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Actions */}
                              <div className="flex gap-3 pt-2">
                                <Button
                                  onClick={() => handleWhatsAppClick(product)}
                                  className="flex-1 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold h-12 rounded-lg shadow-sm transition-colors"
                                >
                                  <MessageCircle className="mr-2 h-4 w-4" />
                                  Enquire on WhatsApp
                                </Button>
                                <Button
                                  onClick={() => handleShareProduct(product)}
                                  variant="outline"
                                  className="border-gray-200 text-gray-500 hover:bg-gray-50 h-12 rounded-lg px-4 transition-colors"
                                >
                                  <Share2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>

                        <Button
                          onClick={() => handleShareProduct(product)}
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-colors"
                        >
                          <Share2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ═══════════════ FOOTER ═══════════════ */}
        <footer id="footer" className="bg-[#1a1a1a] text-gray-400 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
              {/* Brand */}
              <div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {settings?.company_name || 'Copier Bazar'}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Your trusted source for badge making machines, fridge magnet machines, and all raw materials.
                </p>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-semibold text-white mb-4 text-xs uppercase tracking-[0.15em]">Contact</h4>
                <div className="space-y-3">
                  <a href="tel:+918889915080" className="flex items-center gap-3 text-gray-500 hover:text-white transition-colors">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <span className="footer-link text-sm">+91 8889915080</span>
                  </a>
                  <a
                    href="https://wa.me/918889915080"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-500 hover:text-[#25D366] transition-colors"
                  >
                    <MessageCircle className="h-4 w-4 flex-shrink-0" />
                    <span className="footer-link text-sm">WhatsApp Us</span>
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold text-white mb-4 text-xs uppercase tracking-[0.15em]">Quick Links</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="block text-gray-500 hover:text-white transition-colors text-sm footer-link"
                  >
                    Back to Top
                  </button>
                  <button
                    onClick={() => document.getElementById('catalogue')?.scrollIntoView({ behavior: 'smooth' })}
                    className="block text-gray-500 hover:text-white transition-colors text-sm footer-link"
                  >
                    Browse Products
                  </button>
                  <button
                    onClick={() => document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' })}
                    className="block text-gray-500 hover:text-white transition-colors text-sm footer-link"
                  >
                    Categories
                  </button>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
              <p className="text-gray-600 text-xs">
                &copy; {new Date().getFullYear()} {settings?.company_name || 'Copier Bazar'}. All rights reserved.
              </p>
              <p className="text-gray-700 text-xs">
                Machines & Supplies Catalogue
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default HomePage;
